import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: 'bi-speedometer2', testid: 'sidebar-nav-dashboard-link', roles: ['admin'] },
  { to: '/my-candidates', label: 'My Candidates', icon: 'bi-person-badge', testid: 'sidebar-nav-my-candidates-link', roles: ['campaign_manager'] },
  { to: '/my-subscription', label: 'My Subscription', icon: 'bi-credit-card-2-front', testid: 'sidebar-nav-my-subscription-link', roles: ['campaign_manager'] },
  { to: '/candidates', label: 'Candidates', icon: 'bi-people', testid: 'sidebar-nav-candidates-link', roles: ['admin'] },
  { to: '/search', label: 'Search', icon: 'bi-search', testid: 'sidebar-nav-search-link', roles: ['admin'] },
  { section: 'Campaigns', roles: ['admin'] },
  { to: '/admin/elections', label: 'Election Days', icon: 'bi-calendar2-event', testid: 'sidebar-nav-elections-link', roles: ['admin'] },
  { to: '/admin/jurisdictions', label: 'Jurisdictions', icon: 'bi-diagram-3', testid: 'sidebar-nav-jurisdictions-link', roles: ['admin'] },
  { to: '/admin/offices', label: 'Offices', icon: 'bi-bank', testid: 'sidebar-nav-offices-link', roles: ['admin'] },
  { to: '/admin/staging-inbox', label: 'Staging Inbox', icon: 'bi-inbox', testid: 'sidebar-nav-staging-link', roles: ['admin'] },
  { to: '/admin/election-dates', label: 'Election Dates', icon: 'bi-calendar-event', testid: 'sidebar-nav-election-dates-link', roles: ['admin'] },
  { to: '/admin/states', label: 'States & Counties', icon: 'bi-geo-alt', testid: 'sidebar-nav-states-link', roles: ['admin'] },
  { to: '/admin/zip-codes', label: 'Zip Code Management', icon: 'bi-mailbox', testid: 'sidebar-nav-zip-codes-link', roles: ['admin'] },
  { to: '/admin/users', label: 'Users', icon: 'bi-people-fill', testid: 'sidebar-nav-users-link', roles: ['admin'] },
  { section: 'Billing', roles: ['admin'] },
  { to: '/admin/billing', label: 'Subscriptions', icon: 'bi-credit-card', testid: 'sidebar-nav-billing-link', roles: ['admin'] },
  { to: '/tokens', label: 'API Tokens', icon: 'bi-key', testid: 'sidebar-nav-api-tokens-link', roles: ['admin'] },
  { to: '/settings', label: 'Settings', icon: 'bi-gear', testid: 'sidebar-nav-settings-link', roles: ['admin'] },
];

function initials(name) {
  if (!name) return 'A';
  const parts = name.split(' ').filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || name[0].toUpperCase();
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const role = user?.role;
  const visibleNav = NAV_ITEMS.filter((item) => !item.roles || item.roles.includes(role));

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div className="app-shell">
      {open && (
        <div className="sidebar-backdrop" onClick={() => setOpen(false)} />
      )}
      <aside className={`app-sidebar ${open ? 'is-open' : ''}`} data-testid="app-sidebar">
        <div className="sidebar-brand">
          <span className="brand-mark">NV</span>
          <span>National Voter Guide</span>
        </div>
        <nav className="nav flex-column" aria-label="Primary">
          {visibleNav.map((item, idx) => {
            if (item.section) {
              return (
                <div
                  key={`section-${idx}`}
                  className="sidebar-section text-muted text-uppercase px-3 mt-3 mb-1"
                  style={{ fontSize: '0.7rem', letterSpacing: '0.06em', fontWeight: 700 }}
                  data-testid={`sidebar-section-${item.section.toLowerCase()}`}
                >
                  {item.section}
                </div>
              );
            }
            return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={() => setOpen(false)}
              data-testid={item.testid}
            >
              <i className={`bi ${item.icon}`} aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <span className="avatar">{initials(user?.full_name || user?.email)}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.full_name || 'Admin'}
            </div>
            <div className="text-muted" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.email}
            </div>
          </div>
          <Button
            variant="link"
            size="sm"
            onClick={handleLogout}
            title="Sign out"
            data-testid="sidebar-logout-button"
            className="text-muted p-1"
          >
            <i className="bi bi-box-arrow-right" />
          </Button>
        </div>
      </aside>

      <main className="app-main">
        <div className="d-lg-none px-3 py-2 border-bottom bg-white d-flex align-items-center gap-2">
          <Button variant="outline-secondary" size="sm" className="sidebar-toggle" onClick={() => setOpen(true)} data-testid="sidebar-open-button">
            <i className="bi bi-list" /> Menu
          </Button>
          <span className="fw-semibold">National Voter Guide</span>
        </div>
        {children}
      </main>
    </div>
  );
}
